import React from "react";
import { Form, Button, Col, Row, Label, Input, FormGroup, FormFeedback } from "reactstrap";

import PageTypesCreateContainer from "./PageTypesCreateContainer";
import type { PageTypesCreateContainerProps } from "./PageTypesCreateContainer";
import IndexTemplateSelector from "./TemplateSelector/IndexTemplateSelector";
import type { IndexTemplateSelectorProps } from "./TemplateSelector/IndexTemplateSelector";
import ProductSortControl from "./ProductSortControl";
import type { ProductSortControlProps } from "./ProductSortControl";

/** The page draft the wizard edits, reduced to the fields this view reads. */
type PageDraft = {
  sub_pages?: boolean;
  global_title?: string;
  meta_description?: string;
  robots?: string;
  skip_product_page?: boolean;
  product_sort?: ProductSortControlProps["productSort"];
  page_configuration?: { type?: string; path?: string };
};

/** Per-field "is missing" flags from the container's validation. */
type FormChecker = { globalTxt?: boolean; metaDescription?: boolean };

type Step0Props = {
  /** Changes whenever the page-type step must start over. */
  pageTypeStepKey: number;
  onConfigurationChange: PageTypesCreateContainerProps["onConfigurationChange"];
  onErrorChange: PageTypesCreateContainerProps["onErrorChange"];
  isValidationAttempted: boolean;
  onNextStep: () => void;
  derivedPath: string;
  derivedPathNotice: string;
};

type Step1Props = {
  page: PageDraft;
  /** The page-type step's pick, for the template step's product options and template filter. */
  pageTypeSelection: IndexTemplateSelectorProps["pageTypeSelection"];
  /** Changes whenever the template step must start over. */
  templateStepKey: number;
  onTemplateChange: IndexTemplateSelectorProps["onTemplateChange"];
  onContentChange: IndexTemplateSelectorProps["onContentChange"];
  onFormValidation: IndexTemplateSelectorProps["onFormValidation"];
  isValidationAttempted: boolean;
  onCheckboxChange: React.ChangeEventHandler<HTMLInputElement>;
  onInputChange: React.ChangeEventHandler<HTMLInputElement>;
  formchecker: FormChecker;
  showCheckerResult: boolean;
  isFormValid: boolean;
  onPreviousStep: () => void;
  onNextStep: () => void;
  onSaveDraft: () => void;
  properties?: ProductSortControlProps["properties"];
  onProductSortChange: ProductSortControlProps["onChange"];
};

type Step2Props = { onPreviousStep: () => void; onSubmit: () => void };

export type PageFormViewProps = Omit<Step0Props, "derivedPath"> &
  Omit<Step1Props, "properties"> &
  Step2Props & {
    currentStep: number;
    // Passed by PageFormContainer and not read here.
    Currentsite?: unknown;
    properties?: ProductSortControlProps["properties"];
    derivedPathNotice?: string;
  };

const Step0 = ({
  pageTypeStepKey,
  onConfigurationChange,
  onErrorChange,
  isValidationAttempted,
  onNextStep,
  derivedPath,
  derivedPathNotice,
}: Step0Props) => (
  <div>

    <PageTypesCreateContainer
      key={pageTypeStepKey}
      onConfigurationChange={onConfigurationChange}
      onErrorChange={onErrorChange}
      isValidationAttempted={isValidationAttempted}
      derivedPath={derivedPath}
      derivedPathNotice={derivedPathNotice}
    />
    <Button color="primary" onClick={onNextStep}>Next</Button>
  </div>
);

const Step1 = ({
  page,
  pageTypeSelection,
  templateStepKey,
  onTemplateChange,
  onContentChange,
  onFormValidation,
  isValidationAttempted,
  onCheckboxChange,
  onInputChange,            //proper text input handler
  formchecker,
  showCheckerResult,
  isFormValid,
  onPreviousStep,
  onNextStep,
  onSaveDraft,
  properties = [],
  onProductSortChange,
}: Step1Props) => (
  <div>
    <FormGroup>
      <Row className="justify-content-end">
        <Col md="10" className="d-flex justify-content-end align-items-center">
          <Label htmlFor="sub_pages" className="mb-0 mr-2">Include navigation and footer pages:</Label>
          <label className="custom-toggle">
            <input
              type="checkbox"
              id="sub_pages"
              name="sub_pages"
              checked={!!page.sub_pages}
              onChange={onCheckboxChange}
            />
            <span className="custom-toggle-slider rounded-circle" />
          </label>
        </Col>
      </Row>
    </FormGroup>

    <IndexTemplateSelector
      key={templateStepKey}
      pageTypeSelection={pageTypeSelection}
      form_type="pages"
      onTemplateChange={onTemplateChange}
      onContentChange={onContentChange}
      onFormValidation={onFormValidation}
      isValidationAttempted={isValidationAttempted}
    >
      <div className="bg-secondary p-3 m-5">
        <FormGroup className="row">
          <Label className="ml-4 form-control-label medium-text">Title:</Label>
          <Col md="12">
            <Input
              type="text"
              name="global_title"
              value={page.global_title ?? ""}      // always string
              onChange={onInputChange}             // use proper handler
              placeholder="Enter page title"
              className="input-default"
              required
              invalid={!isFormValid && showCheckerResult && formchecker.globalTxt}
            />
            {!isFormValid && showCheckerResult && formchecker.globalTxt && (
              <FormFeedback className="medium-text">Page title is required</FormFeedback>
            )}
          </Col>
        </FormGroup>

        <FormGroup className="row mb-5">
          <Label className="ml-4 form-control-label medium-text">META Description:</Label>
          <Col md="12">
            <Input
              type="textarea"
              name="meta_description"
              value={page.meta_description ?? ""}  // always string
              onChange={onInputChange}             // <-- use proper handler
              placeholder="Enter META description"
              invalid={!isFormValid && showCheckerResult && formchecker.metaDescription}
              required
              rows={4}
            />
            {!isFormValid && showCheckerResult && formchecker.metaDescription && (
              <FormFeedback className="medium-text">META description is required</FormFeedback>
            )}
          </Col>
        </FormGroup>

        <FormGroup className="row mb-5">
          <Label className="ml-4 form-control-label medium-text">Robots / Crawler tag:</Label>
          <Col md="12">
            <Input
              type="select"
              name="robots"
              value={page.robots ?? ""}
              onChange={onInputChange}
            >
              <option value="">Default (index, follow)</option>
              <option value="noindex">No index</option>
              <option value="nofollow">No follow</option>
              <option value="noindex,nofollow">No index, no follow</option>
            </Input>
          </Col>
        </FormGroup>

        {/* SCRUM-1362. Shown on every page type, like the Robots tag above,
            rather than only on product pages: the flag is inert unless the
            page resolves to a product, and conditioning the control on the
            page type is behaviour no acceptance criterion covers. */}
        <FormGroup className="row mb-5">
          <Label htmlFor="skip_product_page" className="ml-4 form-control-label medium-text">
            Skip this product page (send visitors straight to the provider):
          </Label>
          <Col md="12">
            <label className="custom-toggle">
              <input
                type="checkbox"
                id="skip_product_page"
                name="skip_product_page"
                checked={!!page.skip_product_page}
                onChange={onCheckboxChange}
              />
              <span className="custom-toggle-slider rounded-circle" />
            </label>
          </Col>
        </FormGroup>

        {page?.page_configuration?.type === "sub_category" ? (
          <ProductSortControl
            productSort={page.product_sort}
            properties={properties}
            onChange={onProductSortChange}
          />
        ) : null}
      </div>
    </IndexTemplateSelector>

    <Button color="secondary" onClick={onPreviousStep}>Back</Button>
    <Button color="primary" onClick={onNextStep}>Next</Button>
    {/* SCRUM-1240: deliberately on Step1, not Step2. Next is gated on
        isFormValid, so with ContentRequired variables still empty Step2 —
        and its Save Page button — cannot be reached at all. */}
    <Button color="secondary" onClick={onSaveDraft}>Save as draft</Button>
  </div>
);

const Step2 = ({ onPreviousStep, onSubmit }: Step2Props) => (
  <div>
    <Button color="secondary" onClick={onPreviousStep}>Back</Button>
    <Button color="primary" onClick={onSubmit}>Save Page</Button>
  </div>
);

const PageFormView = ({
  currentStep,
  page,
  pageTypeSelection,
  Currentsite,
  templateStepKey,
  pageTypeStepKey,
  onConfigurationChange,
  onErrorChange,
  isValidationAttempted,
  onNextStep,
  onPreviousStep,
  onTemplateChange,
  onContentChange,
  onFormValidation,
  onCheckboxChange,
  onInputChange,                  // <-- plumbed down
  formchecker,
  showCheckerResult,
  isFormValid,
  onSubmit,
  onSaveDraft,
  properties,
  onProductSortChange,
  derivedPathNotice = "",
}: PageFormViewProps) => {
  return (
    <Form>
      <div style={{ display: currentStep === 0 ? "block" : "none" }}>
        <Step0
          pageTypeStepKey={pageTypeStepKey}
          onConfigurationChange={onConfigurationChange}
          onErrorChange={onErrorChange}
          isValidationAttempted={isValidationAttempted}
          onNextStep={onNextStep}
          derivedPath={page?.page_configuration?.path || ""}
          derivedPathNotice={derivedPathNotice}
        />
      </div>

      <div style={{ display: currentStep === 1 ? "block" : "none" }}>
        <Step1
          page={page}
          pageTypeSelection={pageTypeSelection}
          templateStepKey={templateStepKey}
          onTemplateChange={onTemplateChange}
          onContentChange={onContentChange}
          onFormValidation={onFormValidation}
          isValidationAttempted={isValidationAttempted}
          onCheckboxChange={onCheckboxChange}
          onInputChange={onInputChange}          // <-- here
          formchecker={formchecker}
          showCheckerResult={showCheckerResult}
          isFormValid={isFormValid}
          onPreviousStep={onPreviousStep}
          onNextStep={onNextStep}
          onSaveDraft={onSaveDraft}
          properties={properties}
          onProductSortChange={onProductSortChange}
        />
      </div>

      <div style={{ display: currentStep === 2 ? "block" : "none" }}>
        <Step2 onPreviousStep={onPreviousStep} onSubmit={onSubmit} />
      </div>
    </Form>
  );
};

export default PageFormView;
